import {asMap, DeepPartial, removeUndefinedOrNullProps} from '@shared/lib/utils/type_utils';

function isObject(val: unknown): val is Record<string, unknown> {
  return (
    typeof val === 'object' &&
    val !== null &&
    !Array.isArray(val) &&
    !(
      '$$typeof' in val &&
      typeof val.$$typeof === 'symbol' &&
      val.$$typeof.toString() === 'Symbol(react.element)'
    )
  );
}

export function isEmpty(obj: Record<string, unknown>): boolean {
  return Object.keys(obj).length === 0;
}

type DeepMerge<T1 extends Record<string, unknown>, T2 extends Record<string, unknown>> = {
  [K in keyof T1 | keyof T2]: K extends keyof T2
    ? K extends keyof T1 //                        // (K is in T2)
      ? T1[K] extends Record<string, unknown> //   // (K is in T2) + (K is in T1)
        ? T2[K] extends Record<string, unknown> // // (K is in T2) + (K is in T1) + (T1[K] is an object)
          ? DeepMerge<T1[K], T2[K]> //             // (K is in T2) + (K is in T1) + (T1[K] is an object) + (T2[K] is an object)
          : T2[K] //                               // (K is in T2) + (K is in T1) + (T1[K] is an object) + (T2[K] NOT an object)
        : T2[K] //                                 // (K is in T2) + (K is in T1) + (T1[K] NOT an object)
      : T2[K] //                                   // (K is in T2) + (K NOT in T1)
    : K extends keyof T1 //                        // (K NOT in T2)
      ? T1[K] //                                   // (K NOT in T2) + (K in T1)
      : never; //                                  // (K NOT in T2) + (K NOT in T1)
};

/**
 * Merges two objects deeply, preferring values from obj2 when both have a value defined for the same attribute.
 * Original objects are not modified.
 * Arrays are considered a primary value and are not merged/concatenated between the two objects.
 * @param obj1 - The first object to merge.
 * @param obj2 - The second object to merge.
 * @returns A new object that is the result of merging obj1 and obj2 deeply.
 */
export function deepMerge<O1 extends {[K in keyof O1]: O1[K]}, O2 extends {[K in keyof O2]: O2[K]}>(
  obj1: O1,
  obj2: O2
): DeepMerge<O1, O2> {
  const res = {...obj1} as DeepMerge<O1, O2>;
  for (const key of Object.keys(obj2)) {
    const o1 = res as Record<string, unknown>;
    const o2 = obj2 as Record<string, unknown>;
    const targetValue = o1[key];
    const sourceValue = o2[key];
    o1[key] =
      // We are only merging when both are objects, otherwise we give priority to the value in obj2
      // unless it is undefined (even if set explicitly on the obj1 object, not just the missing property)
      isObject(targetValue) && isObject(sourceValue)
        ? deepMerge(targetValue, sourceValue)
        : sourceValue === undefined
          ? targetValue
          : sourceValue;
  }
  return res;
}

// biome-ignore lint/suspicious/noExplicitAny: <no-explanation>
export function extendObject<T extends Record<string, any>>(target: T, source: DeepPartial<T>): T {
  return deepMerge(target, source) as T;
}

export function deepEqual(obj1: unknown, obj2: unknown): boolean {
  if (typeof obj1 !== typeof obj2) {
    return false;
  }
  if (typeof obj1 !== 'object' || typeof obj2 !== 'object') {
    return obj1 === obj2;
  }

  if (obj1 === null && obj2 === null) {
    return true;
  }
  if (obj1 === null || obj2 === null) {
    return false;
  }

  if (Array.isArray(obj1) && Array.isArray(obj2)) {
    if (obj1.length !== obj2.length) {
      return false;
    }
    for (const [i, element] of obj1.entries()) {
      if (!deepEqual(element, obj2[i])) {
        return false;
      }
    }
    return true;
  }

  if (Array.isArray(obj1) || Array.isArray(obj2)) {
    return false;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  if (keys1.length !== keys2.length) {
    return false;
  }
  for (const key of keys1) {
    if (
      !deepEqual((obj1 as Record<string, unknown>)[key], (obj2 as Record<string, unknown>)[key])
    ) {
      return false;
    }
  }
  return true;
}

export function deepEqualWithCleanup(obj1: unknown, obj2: unknown): boolean {
  return deepEqual(
    removeUndefinedOrNullProps(asMap(obj1, {})),
    removeUndefinedOrNullProps(asMap(obj2, {}))
  );
}
